/**
 * Encoder/Decoder für den 18-Byte-Cancel-Token (Version 0x01).
 *
 * Layout (siehe `docs/cancel-token-wire-format.md`):
 * - Byte 0       (8 Bit):  `version` (= TOKEN_VERSION).
 * - Bytes 1–5    (40 Bit): `appointmentId` (LE).
 * - Bytes 6–10   (40 Bit): `timeSlotSecondsFromEpoch` (LE).
 * - Bytes 11–12  (16 Bit): `locationId` (LE).
 * - Bytes 13–17  (40 Bit): `pinFingerprint` (Roh-Bytes des SPKI-SHA-256-Prefix).
 *
 * Ausgabe: 24 Base64URL-Zeichen ohne Padding.
 */

import {
  APPOINTMENT_ID_BIT_LIMIT,
  LOCATION_BIT_LIMIT,
  PIN_FINGERPRINT_BYTE_LENGTH,
  TIMESLOT_BIT_LIMIT,
  TIMESLOT_EPOCH_UNIX,
  TOKEN_BASE64URL_LENGTH,
  TOKEN_BYTE_LENGTH,
  TOKEN_PLAUSIBILITY_FUTURE_MS,
  TOKEN_PLAUSIBILITY_PAST_MS,
  TOKEN_VERSION,
} from './token.constants.js';
import { decodeBase64Url, encodeBase64Url } from './safe-base64.js';
import { TokenError } from './token-error.js';
import type { TokenDecoded } from './token-decoded.type.js';
import type { TokenInput } from './token.interface.js';

const VERSION_BYTE_OFFSET = 0;
const APPOINTMENT_ID_BYTE_OFFSET = 1;
const APPOINTMENT_ID_BYTE_LENGTH = 5;
const TIMESLOT_BYTE_OFFSET = 6;
const TIMESLOT_BYTE_LENGTH = 5;
const LOCATION_BYTE_OFFSET = 11;
const LOCATION_BYTE_LENGTH = 2;
const PIN_FINGERPRINT_BYTE_OFFSET = 13;
const BYTE_MASK = 0xff;
const MILLISECONDS_PER_SECOND = 1000;

/**
 * Kodiert ein Cancel-Token in Base64URL-Form ohne Padding (24 Zeichen).
 *
 * @throws {TokenError} mit `kind: 'FIELD_OVERFLOW'`, wenn ein Feld sein
 *         Bit-Limit überschreitet, negativ ist, oder `pinFingerprint` eine
 *         falsche Länge hat.
 */
export function encodeToken(input: TokenInput): string {
  validateAppointmentId(input.appointmentId);
  validateTimeSlot(input.timeSlotSecondsFromEpoch);
  validateLocationId(input.locationId);
  validatePinFingerprint(input.pinFingerprint);

  const bytes = new Uint8Array(TOKEN_BYTE_LENGTH);
  bytes[VERSION_BYTE_OFFSET] = TOKEN_VERSION;
  writeBigIntLittleEndian(
    bytes,
    APPOINTMENT_ID_BYTE_OFFSET,
    APPOINTMENT_ID_BYTE_LENGTH,
    input.appointmentId,
  );
  writeNumberLittleEndian(
    bytes,
    TIMESLOT_BYTE_OFFSET,
    TIMESLOT_BYTE_LENGTH,
    input.timeSlotSecondsFromEpoch,
  );
  writeNumberLittleEndian(bytes, LOCATION_BYTE_OFFSET, LOCATION_BYTE_LENGTH, input.locationId);
  bytes.set(input.pinFingerprint, PIN_FINGERPRINT_BYTE_OFFSET);
  return encodeBase64Url(bytes);
}

/**
 * Dekodiert ein 24-Zeichen-Base64URL-Token in seine Felder.
 *
 * Führt einen Plausibility-Check auf `timeSlot` durch (siehe
 * `TOKEN_PLAUSIBILITY_PAST_MS` / `TOKEN_PLAUSIBILITY_FUTURE_MS`).
 *
 * @param token Base64URL-Token (24 Zeichen, ohne Padding).
 * @param nowMs Referenzzeitpunkt in Millisekunden (default `Date.now()`).
 * @throws {TokenError} mit
 *         - `INVALID_FORMAT` bei falscher Länge oder Zeichensatz,
 *         - `VERSION_MISMATCH` wenn das Versions-Byte nicht `TOKEN_VERSION` ist,
 *         - `TIMESLOT_OUT_OF_RANGE` bei Plausibility-Verletzung.
 */
export function decodeToken(token: string, nowMs: number = Date.now()): TokenDecoded {
  assertTokenFormat(token);
  const bytes = decodeTokenBytes(token);
  assertTokenVersion(bytes[VERSION_BYTE_OFFSET] ?? 0);

  const appointmentId = readBigIntLittleEndian(
    bytes,
    APPOINTMENT_ID_BYTE_OFFSET,
    APPOINTMENT_ID_BYTE_LENGTH,
  );
  const timeSlotSecondsFromEpoch = readNumberLittleEndian(
    bytes,
    TIMESLOT_BYTE_OFFSET,
    TIMESLOT_BYTE_LENGTH,
  );
  const locationId = readNumberLittleEndian(bytes, LOCATION_BYTE_OFFSET, LOCATION_BYTE_LENGTH);
  const pinFingerprint = bytes.slice(
    PIN_FINGERPRINT_BYTE_OFFSET,
    PIN_FINGERPRINT_BYTE_OFFSET + PIN_FINGERPRINT_BYTE_LENGTH,
  );

  assertTimeSlotPlausibility(timeSlotSecondsFromEpoch, nowMs);
  return { appointmentId, timeSlotSecondsFromEpoch, locationId, pinFingerprint };
}

function assertTokenFormat(token: string): void {
  if (typeof token !== 'string' || token.length !== TOKEN_BASE64URL_LENGTH) {
    throw new TokenError(
      'INVALID_FORMAT',
      `Token must be exactly ${String(TOKEN_BASE64URL_LENGTH)} Base64URL characters`,
    );
  }
}

function decodeTokenBytes(token: string): Uint8Array {
  let bytes: Uint8Array;
  try {
    bytes = decodeBase64Url(token);
  } catch {
    throw new TokenError('INVALID_FORMAT', 'Token contains invalid Base64URL characters');
  }
  if (bytes.length !== TOKEN_BYTE_LENGTH) {
    throw new TokenError(
      'INVALID_FORMAT',
      `Decoded token has wrong byte length (expected ${String(TOKEN_BYTE_LENGTH)})`,
    );
  }
  return bytes;
}

function assertTokenVersion(versionByte: number): void {
  if (versionByte !== TOKEN_VERSION) {
    throw new TokenError(
      'VERSION_MISMATCH',
      `Unsupported token version 0x${versionByte.toString(16).padStart(2, '0')}`,
    );
  }
}

function validateAppointmentId(value: bigint): void {
  if (typeof value !== 'bigint') {
    throw new TokenError('FIELD_OVERFLOW', 'appointmentId must be a bigint', 'appointmentId');
  }
  if (value < 0n || value >= APPOINTMENT_ID_BIT_LIMIT) {
    throw new TokenError(
      'FIELD_OVERFLOW',
      'appointmentId exceeds 40-bit unsigned range',
      'appointmentId',
    );
  }
}

function validateTimeSlot(value: number): void {
  if (!Number.isInteger(value) || value < 0 || value >= TIMESLOT_BIT_LIMIT) {
    throw new TokenError(
      'FIELD_OVERFLOW',
      'timeSlotSecondsFromEpoch must be a non-negative integer below 2^40',
      'timeSlotSecondsFromEpoch',
    );
  }
}

function validateLocationId(value: number): void {
  if (!Number.isInteger(value) || value < 0 || value >= LOCATION_BIT_LIMIT) {
    throw new TokenError(
      'FIELD_OVERFLOW',
      'locationId must be a non-negative integer below 2^16',
      'locationId',
    );
  }
}

function validatePinFingerprint(value: Uint8Array): void {
  if (!(value instanceof Uint8Array) || value.length !== PIN_FINGERPRINT_BYTE_LENGTH) {
    throw new TokenError(
      'FIELD_OVERFLOW',
      `pinFingerprint must be a Uint8Array of length ${String(PIN_FINGERPRINT_BYTE_LENGTH)}`,
      'pinFingerprint',
    );
  }
}

function assertTimeSlotPlausibility(timeSlotSecondsFromEpoch: number, nowMs: number): void {
  const timeSlotMs = (TIMESLOT_EPOCH_UNIX + timeSlotSecondsFromEpoch) * MILLISECONDS_PER_SECOND;
  const minMs = nowMs - TOKEN_PLAUSIBILITY_PAST_MS;
  const maxMs = nowMs + TOKEN_PLAUSIBILITY_FUTURE_MS;
  if (timeSlotMs < minMs || timeSlotMs > maxMs) {
    throw new TokenError(
      'TIMESLOT_OUT_OF_RANGE',
      'timeSlot is outside the plausible appointment window',
      'timeSlotSecondsFromEpoch',
    );
  }
}

function writeNumberLittleEndian(
  bytes: Uint8Array,
  offset: number,
  byteLength: number,
  value: number,
): void {
  let remaining = value;
  for (let index = 0; index < byteLength; index += 1) {
    bytes[offset + index] = remaining & BYTE_MASK;
    remaining = Math.floor(remaining / (BYTE_MASK + 1));
  }
}

function writeBigIntLittleEndian(
  bytes: Uint8Array,
  offset: number,
  byteLength: number,
  value: bigint,
): void {
  let remaining = value;
  const byteMaskBigInt = BigInt(BYTE_MASK);
  for (let index = 0; index < byteLength; index += 1) {
    bytes[offset + index] = Number(remaining & byteMaskBigInt);
    remaining >>= 8n;
  }
}

function readNumberLittleEndian(bytes: Uint8Array, offset: number, byteLength: number): number {
  let value = 0;
  let multiplier = 1;
  for (let index = 0; index < byteLength; index += 1) {
    value += (bytes[offset + index] ?? 0) * multiplier;
    multiplier *= BYTE_MASK + 1;
  }
  return value;
}

function readBigIntLittleEndian(bytes: Uint8Array, offset: number, byteLength: number): bigint {
  let value = 0n;
  for (let index = byteLength - 1; index >= 0; index -= 1) {
    value = (value << 8n) | BigInt(bytes[offset + index] ?? 0);
  }
  return value;
}
